import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { runDatasetQuery } from "@/lib/reporting/engine.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { KpiCard } from "@/components/reporting/kpi-card";
import { ChartCard } from "@/components/reporting/chart-card";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Activity, Users, Timer, Boxes } from "lucide-react";
import { useMemo } from "react";
import { useReportsRange } from "@/hooks/use-reports-range";

export const Route = createFileRoute("/_authenticated/app/reports/warehouse")({ component: WarehouseDash });

function WarehouseDash() {
  const { tenantId } = useScope();
  const run = useServerFn(runDatasetQuery);
  const { sinceIso: since, untilIso: until, days: windowDays } = useReportsRange(7);
  const rangeFilter = (column: string) =>
    until
      ? { type: "and" as const, conditions: [
          { type: "condition" as const, column, op: "gte" as const, value: since },
          { type: "condition" as const, column, op: "lte" as const, value: until },
        ] }
      : { type: "condition" as const, column, op: "gte" as const, value: since };

  const { data: laborByActivity, isLoading: l1 } = useQuery({
    queryKey: ["rpt-labor-activity", tenantId, since, until],
    enabled: !!tenantId,
    queryFn: () => run({ data: {
      tenantId: tenantId!, datasetKey: "labor",
      filters: rangeFilter("started_at"),
      group: ["activity"],
      aggregations: [
        { column: "units", fn: "sum", alias: "units" },
        { column: "duration_seconds", fn: "sum", alias: "seconds" },
        { column: "id", fn: "count", alias: "events" },
      ],
      limit: 200, offset: 0,
    }}),
  });

  const { data: laborByUser, isLoading: l2 } = useQuery({
    queryKey: ["rpt-labor-user", tenantId, since, until],
    enabled: !!tenantId,
    queryFn: () => run({ data: {
      tenantId: tenantId!, datasetKey: "labor",
      filters: rangeFilter("started_at"),
      group: ["user_id"],
      aggregations: [
        { column: "units", fn: "sum", alias: "units" },
        { column: "duration_seconds", fn: "sum", alias: "seconds" },
      ],
      limit: 500, offset: 0,
    }}),
  });

  const totals = useMemo(() => {
    const rows = laborByActivity?.rows ?? [];
    let units = 0, seconds = 0, events = 0;
    for (const r of rows) {
      units += Number(r.units ?? 0);
      seconds += Number(r.seconds ?? 0);
      events += Number(r.events ?? 0);
    }
    const uph = seconds > 0 ? Math.round((units / seconds) * 3600) : 0;
    return { units, hours: Math.round(seconds / 3600), events, uph };
  }, [laborByActivity]);

  const activityChart = (laborByActivity?.rows ?? []).map((r: any) => ({
    activity: r.activity ?? "—", units: Number(r.units ?? 0), hours: +(Number(r.seconds ?? 0) / 3600).toFixed(1),
  }));
  const topUsers = [...(laborByUser?.rows ?? [])]
    .map((r: any) => ({ user: String(r.user_id ?? "—").slice(0, 8), units: Number(r.units ?? 0), hours: +(Number(r.seconds ?? 0) / 3600).toFixed(1) }))
    .sort((a, b) => b.units - a.units).slice(0, 10);

  return (
    <>
      <PageHeader title="Warehouse Operations" description={`Labor and throughput over the selected window (${windowDays} day${windowDays === 1 ? "" : "s"}).`} />
      <div className="p-4 sm:p-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Units handled" value={totals.units} icon={<Boxes className="h-4 w-4" />} loading={l1} />
          <KpiCard label="Labor hours" value={totals.hours} icon={<Timer className="h-4 w-4" />} loading={l1} />
          <KpiCard label="Activity events" value={totals.events} icon={<Activity className="h-4 w-4" />} loading={l1} />
          <KpiCard label="Units / hour" value={totals.uph} icon={<Users className="h-4 w-4" />} loading={l1} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Throughput by Activity">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={activityChart}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="activity" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Legend />
                <Bar dataKey="units" fill="var(--primary)" name="Units" />
                <Bar dataKey="hours" fill="var(--muted-foreground)" name="Hours" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Top Operators (by Units)">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topUsers} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis type="number" fontSize={11} />
                <YAxis type="category" dataKey="user" fontSize={11} width={80} />
                <Tooltip />
                <Bar dataKey="units" fill="var(--primary)" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        {(l2 || (laborByUser?.rows ?? []).length === 0) && !l2 && (
          <p className="text-sm text-muted-foreground text-center">No labor events recorded yet.</p>
        )}
      </div>
    </>
  );
}